import { Trans, useTranslation } from 'next-i18next'

import {
  Heading3,
  LinkItem,
  LinkList,
  LinkText,
  Paragraph,
  Section3
} from '@/features/Listings/seo/elements'
import AverageRentFaq from '@/features/Listings/seo/search-results-content/content/average-rent-faq'
import ContentTemplate from '@/features/Listings/seo/search-results-content/content/city/content-template'
import getFilterLinks from '@/features/Listings/seo/search-results-content/content/get-filter-links'

import Amenities from '../amenities'
import RentingIn from '../renting-in'

const Dorval = ({
  filterSlug,
  isCanadaPage,
  cityName,
  cityNameWithState,
  cityData,
  isChinese
}) => {
  const { t } = useTranslation()
  const { state, slug: citySlug } = cityData
  const links = getFilterLinks(isCanadaPage, citySlug)

  const neighborhoodsLink = isChinese
    ? 'https://liv.rent/blog/zh/%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/'
    : 'https://liv.rent/blog/neighbourhoods/'

  const getAbout = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.dorval:About Dorval Paragraph 1">
            Dorval, a charming suburb on the Island of Montreal in{' '}
            <LinkText href="/rental-listings/quebec">Quebec (QC)</LinkText>,
            harmonizes suburban tranquility and urban convenience found in the
            essence of QC’s diverse locales. Nestled along Lake Saint-Louis,
            Dorval offers scenic waterfront views and a mix of residential
            communities, from Dorval Island’s exclusivity to diverse
            neighborhoods with housing options ranging from single-family homes
            to apartments. Its proximity to Montreal and major highways,
            including the Pierre Elliott Trudeau International Airport, ensures
            easy accessibility.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo.city.dorval:About Dorval Paragraph 2">
            The town’s parks, green spaces, and recreational facilities provide
            residents with outdoor havens, while cultural events and festivals
            foster community engagement. Dorval’s bilingualism, historical
            heritage, and educational and healthcare services contribute to its
            balanced lifestyle. Embodying the allure of QC’s diverse locales,
            Dorval exemplifies the seamless blend of suburban living and urban
            access prevalent in{' '}
            <LinkText href={neighborhoodsLink}>
              cities and neighbourhoods in QC
            </LinkText>
            .
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentingIn = () => {
    return (
      <RentingIn
        filterSlug={filterSlug}
        cityName={cityName}
        rentingTipsLink={neighborhoodsLink}
      />
    )
  }

  const getAverageRent = () => {
    return (
      <AverageRentFaq
        filterSlug={filterSlug}
        isCanadaPage={isCanadaPage}
        cityName={cityName}
        cityNameWithState={cityNameWithState}
        citySlug={citySlug}
        rentAmounts={{
          bed1: '$1,200 - $2,400',
          bed2: '$1,300 - $2,700',
          bed3: '$2,300 - $3,700'
        }}
        rentReportCity={t('cities:city_name_montreal')}
        rentReportLink="https://liv.rent/blog/rent-reports/montreal/"
        rentReportLinkZh="https://liv.rent/blog/zh/%e7%a7%9f%e9%87%91%e7%bb%9f%e8%ae%a1/%e8%92%99%e7%89%b9%e5%88%a9%e5%b0%94/"
      />
    )
  }

  const getApartmentHunting = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.dorval:Apartment Hunting in Dorval Paragraph 1">
            Compared to cities like{' '}
            <LinkText href="/rental-listings/city/vancouver">
              Vancouver
            </LinkText>{' '}
            and <LinkText href="/rental-listing/city/toronto">Toronto</LinkText>
            , it’s easier to find cheap rentals in Dorval – although{' '}
            <LinkText href={links.apartmentsUnder1000}>
              apartments for rent under $1000
            </LinkText>{' '}
            are still uncommon. To start your search, you can refer to our
            guides on{' '}
            <LinkText href={neighborhoodsLink}>
              neighbourhoods in Canada
            </LinkText>
            .
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo:Apartment Hunting in LOCATION Paragraph 2">
            In addition to choosing your location, it’s important to know what
            rental types are available in {{ cityName }} (e.g.{' '}
            <LinkText href={links.bed1}>1 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed2}>2 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed3}>3 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.studio}>studio apartments</LinkText>) and the
            average rent for each property type. Refer below to figure out your
            options - ranging from{' '}
            <LinkText href={links.furnished}>furnished rentals</LinkText> and{' '}
            <LinkText href={links.unfurnished}>unfurnished rentals</LinkText> to{' '}
            <LinkText href={links.longTerm}>long term rentals</LinkText> and{' '}
            <LinkText href={links.shortTerm}>short term rentals</LinkText>.
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentalTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Apartments for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Apartments for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.dorval:Apartments for rent Dorval Paragraph">
              In Dorval, apartments are a common form of housing you’ll find.
              Average rent prices for apartments depend on where you’re looking.{' '}
              <LinkText href={links.apartment}>Apartments for rent</LinkText> in
              urban areas close to amenities and transportation are typically
              more expensive due to higher demand. Apartments generally require
              much less maintenance than houses, making them ideal for renters
              who don’t need as much space.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Houses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Houses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.dorval:Houses for rent Dorval Paragraph">
              Entire <LinkText href={links.house}>houses for rent</LinkText> are
              less common in Dorval but can still be found. They are suitable
              for those with a higher budget, need extra space, or who have
              multiple roommates. Both partial houses and entire houses for rent
              are common, as well as both new builds and older homes.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Townhouses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Townhouses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.dorval:Townhouses for rent Dorval Paragraph">
              <LinkText href={links.townhouse}>
                Townhouses for rent in Dorval
              </LinkText>{' '}
              are more common in residential communities. Combining the best
              qualities of both apartments and whole houses, townhouses for rent
              offer an attractive combination of freedom and space, generally
              coming with your own private yard and entrance.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Basements for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Basements for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Basements for rent CITY Paragraph">
              For those looking to find affordable rental housing in this
              increasingly expensive city, basements for rent in {{ cityName }}{' '}
              pose an attractive alternative. Since house prices are so
              expensive, homeowners will sometimes rent out their basements as a
              way to offset their mortgage payments. This makes it easy for
              renters to find relatively inexpensive basements for rent in all
              areas of the city.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Condos for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Condos for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.dorval:Condos for rent Dorval Paragraph">
              <LinkText href={links.apartment}>Condos for rent</LinkText> are
              also popular in Dorval, offering a more upscale living experience,
              often with additional amenities like gyms or pools. Rental prices
              will vary based on location, size, and facilities. Often situated
              in areas with convenient access to city features, these are
              suitable for those looking for a more maintenance-free lifestyle.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Rooms for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Rooms for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Rooms for rent CITY Paragraph">
              Sharing a house or apartment is practically essential for those
              looking for cheap rent.{' '}
              <LinkText href={links.room}>
                Rooms for rent in {{ cityName }}
              </LinkText>{' '}
              are a great way to experience the best of the city on a budget,
              and can offer you the opportunity to live in neighbourhoods that
              would otherwise be unaffordable.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getSpecialTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Pet friendly rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Pet friendly rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Pet friendly rentals CITY Paragraph">
              Though not every landlord is keen on allowing pets, there are a
              good number of{' '}
              <LinkText href={links.petFriendly}>
                pet-friendly rentals in {{ cityName }}
              </LinkText>{' '}
              for those with furry friends. Both{' '}
              <LinkText href={links.dogFriendly}>dog-friendly</LinkText> and{' '}
              <LinkText href={links.catFriendly}>cat-friendly</LinkText> rental
              units are available, as well as properties where both are allowed.
              Keep in mind that some {{ cityName }} neighbourhoods are better
              suited to those with pets, and adjust your search accordingly.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Furnished rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Furnished rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Furnished rentals LOCATION Paragraph">
              If you’re new to town or just haven’t had a chance to pick up
              furniture of your own yet, not to worry – there are plenty of{' '}
              <LinkText href={links.furnished}>furnished rentals</LinkText>{' '}
              available in {{ cityName }}. Renters seeking furnished units
              should search for these units using filters, but do be prepared to
              pay more in rent. There are{' '}
              <LinkText href="https://liv.rent/blog/renters/renting-furnished-unfurnished/">
                pros and cons to renting furnished units compared to unfurnished
                units
              </LinkText>
              , so deciding whether it’s worth it to buy furniture when moving
              is a good first step.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Short term rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Short term rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.dorval:Short term rentals Dorval Paragraph">
              Dorval, Quebec, offers various employment prospects in sectors
              like transportation, logistics, and services, given its proximity
              to Montréal–Trudeau International Airport. While the city itself
              doesn’t host extensive academic institutions, its close proximity
              to Montreal allows residents easy access to numerous renowned
              universities and colleges. In Dorval,{' '}
              <LinkText href={links.shortTerm}>short-term rentals</LinkText> see
              a surge, particularly during peak travel seasons, catering to
              tourists and business travelers. This type of accommodation is
              often sought after by individuals needing temporary housing or
              those seeking the convenience of a commitment-free arrangement.
              The flexibility provided by short-term rentals can be attractive,
              and is a key factor in{' '}
              <LinkText href="https://liv.rent/blog/landlords/short-term-vs-long-term-rentals-which-are-right-for-you/">
                deciding whether or not short term rentals are suitable
              </LinkText>
              .
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Cheap rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Cheap rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Cheap rentals LOCATION 2 Paragraph">
              {{ cityName }} is known for its inexpensive rent, making it
              relatively easy to find something within your budget. If you’re
              looking for{' '}
              <LinkText href={links.under1000}>
                cheap rentals in {{ cityNameWithState }}
              </LinkText>
              , try finding a roommate or two and exploring shared housing; or
              search for <LinkText href={links.room}>rooms for rent</LinkText>.
              It’s worth noting that there are neighbourhoods in {{ cityName }}{' '}
              where rent is considerably cheaper, so exploring these options is
              a good idea as well.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getLiving = () => {
    return (
      <>
        <Trans i18nKey="seo.city.dorval:Living in Dorval Paragraph">
          <Paragraph>
            Dorval, Quebec, presents a harmonious blend of suburban tranquility
            and the convenience of urban amenities, given its strategic location
            near Montreal. Known for its renter-friendly market, Dorval attracts
            a diverse populace, including young professionals, families, and
            frequent travelers, largely due to its proximity to the
            Montréal–Trudeau International Airport and various multinational
            corporations. The city is suitable for those who cherish waterfront
            views, green spaces, and activities, as it’s known for the beautiful
            Dorval Island and numerous parks, offering residents a respite from
            the bustling city life. Cultural landmarks, like the Dorval Museum
            of Local History and Heritage, add to the city’s charm, making it an
            inviting community for those interested in regional history.
            Moreover, Dorval hosts several community events and festivals
            throughout the year, enhancing the tight-knit community feel in this
            scenic lakeside city.
          </Paragraph>
          <Paragraph>
            If you’re not sure that Dorval is the right city for you, check out
            some of these resources comparing the different cities.
          </Paragraph>
        </Trans>
        {isChinese ? (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e5%8a%a0%e6%8b%bf%e5%a4%a7%e7%95%99%e5%ad%a65%e5%a4%a7%e5%9f%8e%e5%b8%82-%e7%a7%9f%e6%88%bf%e3%80%81%e5%ad%a6%e8%b4%b9%e3%80%81%e5%b7%a5%e4%bd%9c%e3%80%81%e7%ad%be%e8%af%81%e8%af%a6%e8%a7%a3/">
              加拿大最佳移民城市
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e6%b8%a9%e5%93%a5%e5%8d%8e%e3%80%81%e5%a4%9a%e4%bc%a6%e5%a4%9a%e6%af%94%e8%be%83/">
              温哥华 vs 多伦多：生活成本比较
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/%E8%A1%97%E5%8C%BA%E6%8C%87%E5%8D%97/%E6%B8%A9%E5%93%A5%E5%8D%8E%E5%92%8C%E7%B4%A0%E9%87%8C%E5%B1%85%E4%BD%8F%E6%88%90%E6%9C%AC%E6%AF%94%E8%BE%83/">
              温哥华 vs 素里：生活成本比较
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/toronto-zh/2021%e7%89%88%e5%a4%9a%e4%bc%a6%e5%a4%9a%e3%80%81%e8%92%99%e7%89%b9%e5%88%a9%e5%b0%94%e7%94%9f%e6%b4%bb%e6%88%90%e6%9c%ac%e6%af%94%e8%be%83/">
              多伦多 vs 蒙特利尔：生活成本比较
            </LinkItem>
          </LinkList>
        ) : (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/2020/11/best-canadian-cities-for-immigration/">
              Best Canadian cities for immigration
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/2019/11/vancouver-or-toronto-a-cost-of-living-comparison/">
              Vancouver vs Toronto: Cost of living comparison
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/2021/02/surrey-vs-vancouver-bc-comparison/">
              Vancouver vs Surrey: Cost of living comparison
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/2021/04/toronto-vs-montreal-cost-of-living-comparison/">
              Toronto vs Montreal: Cost of living comparison
            </LinkItem>
          </LinkList>
        )}
      </>
    )
  }

  const getPopularHoods = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.dorval:Popular Neighbourhoods Dorval Paragraph">
          Like any city, Dorval is made up of smaller neighbourhoods with their
          own distinct features. Some of the most popular neighbourhoods include
          Dorval Village, Lakeside, Dorval Island, Westminster, Dorval North,
          Dorval South, and Strathmore. For more info about Dorval, you can
          consult this{' '}
          <LinkText href={neighborhoodsLink}>
            guide on QC neighbourhoods
          </LinkText>
          .
        </Trans>
      </Paragraph>
    )
  }

  const getLandmarks = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.dorval:Local Landmarks Dorval Paragraph">
          Dorval, Quebec, is home to a variety of landmarks that embody both its
          rich history and natural beauty. One of the standout historical sites
          is the Dorval Museum of Local History and Heritage, set in an early
          20th-century house, offering visitors a glimpse into the city’s past
          through various exhibits. Not far from the urban conveniences, the
          serene Pine Beach Park opens up a vista of Lake Saint-Louis, a popular
          spot for relaxation and various water activities. Another significant
          landmark is the Dorval Island, a small and exclusive residential area
          steeped in history and accessible only by ferry, highlighting the
          city’s unique blend of urban and rural landscapes. Additionally,
          travelers and locals frequently acknowledge the Montréal–Pierre
          Elliott Trudeau International Airport, one of Canada’s busiest
          airports, as a modern landmark vital to the region’s connectivity and
          cosmopolitan vibe.
        </Trans>
      </Paragraph>
    )
  }

  const content = {
    about: getAbout(),
    rentingIn: getRentingIn(),
    averageRent: getAverageRent(),
    apartmentHunting: getApartmentHunting(),
    rentalTypes: getRentalTypes(),
    specialTypes: getSpecialTypes(),
    amenities: (
      <Amenities cityName={cityName} filterSlug={filterSlug} links={links} />
    ),
    living: getLiving(),
    popularHoods: getPopularHoods(),
    landmarks: getLandmarks()
  }

  return (
    <ContentTemplate
      content={content}
      filterSlug={filterSlug}
      isCanadaPage={isCanadaPage}
      cityName={cityName}
      cityNameWithState={cityNameWithState}
      citySlug={citySlug}
      state={state}
    />
  )
}

export default Dorval
